import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Mail, CheckCircle2, Info } from "lucide-react";
import { DashboardShell, PageHead } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { journeyApi, apiError } from "@/lib/journeyApi";
import { track } from "@/lib/analytics";

const FAQS = [
  {
    q: "How does The Paycheck Edit work?",
    a: "When money lands, you enter the amount that actually arrived. The Paycheck Edit works out what has to stay available for your real-life obligations before your next paycheck, what to keep protected, and what is free for your next move. It proposes an Edit. It never moves money and never touches your accounts.",
  },
  {
    q: "What is an Edit?",
    a: "An Edit is a proposed, single-move allocation for the money that is free after obligations and protection. For debt, it targets your smallest eligible balance first. It is only a proposal until you confirm you actually made the payment.",
  },
  {
    q: "Does The Paycheck Edit ever move my money?",
    a: "No. The Paycheck Edit is planning only. Nothing is transferred, no bank is connected, and your balances change only after you confirm a payment you made yourself.",
  },
  {
    q: "What are the four phases?",
    a: "PROTECT builds a cushion so an income gap does not derail you. ELIMINATE clears consumer debt smallest balance first. BUILD directs money toward a goal you choose, like a cash reserve, Mortgage Freedom, or Financial Independence. FREEDOM is the educational scenario planning that helps you see the long game.",
  },
  {
    q: "What is the difference between my Emergency Fund and my Protected Runway?",
    a: "Your Emergency Fund covers one-off spending shocks. Your Protected Runway covers essential living costs if your income is interrupted. They are tracked separately and neither is ever spent automatically.",
  },
  {
    q: "What is expected income?",
    a: "If your income is variable or hybrid, you can note money you expect soon, like a commission or an invoice. It is a plan only. Nothing is counted until the money lands and you confirm the real amount.",
  },
  {
    q: "How is the Mortgage Freedom scenario calculated?",
    a: "It is a deterministic fixed-rate estimate from the figures you enter: balance, rate, payment or term. It shows your payoff timeline and how much sooner you could be mortgage-free with extra payments. It assumes your rate and payment stay the same and guarantees nothing.",
  },
  {
    q: "Is the Financial Independence planner investment advice?",
    a: "No. It is an educational, assumption-based scenario. You choose the withdrawal rate, your current assets, and any growth assumption. The Paycheck Edit does not recommend investments, securities, or asset allocations, and guarantees no outcome.",
  },
  {
    q: "How much does The Paycheck Edit cost?",
    a: "The Paycheck Edit is a one-time purchase of $147. There is no subscription and no recurring charge.",
  },
  {
    q: "How is my data handled?",
    a: "Your information is used to power your own planning. You can export a summary at any time, delete your financial data while keeping your access, or delete your account entirely from Profile under Privacy & Data.",
  },
];

const CATEGORIES = [
  { key: "question", label: "General question" },
  { key: "billing", label: "Billing & purchase" },
  { key: "bug", label: "Something's broken" },
  { key: "feedback", label: "Feedback / idea" },
  { key: "account", label: "My account" },
  { key: "other", label: "Other" },
];

const inputCls = "mt-1 min-h-11 w-full rounded-xl pe-surface px-3.5 text-[#171717] outline-none focus:border-[#2F6B57]";
const labelCls = "text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]";

function ContactForm() {
  const [category, setCategory] = useState("question");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null); // null | {emailed, configured}

  const submit = async () => {
    if (!subject.trim() || !message.trim()) return toast.error("Add a subject and a message.");
    setBusy(true);
    try {
      const res = await journeyApi.contactSupport({ category, subject: subject.trim(), message: message.trim() });
      setSent({ emailed: res.emailed, configured: res.configured });
      track("support_submitted", { category });
      toast.success("Thanks. We've received your message.");
    } catch (e) {
      toast.error(apiError(e, "Could not send your message."));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-2xl border border-[#2F6B57]/40 bg-[#DDE7DF] p-6 text-center" data-testid="support-sent">
        <CheckCircle2 className="mx-auto h-10 w-10 text-[#2F6B57]" />
        <p className="mt-3 font-serif text-2xl text-[#164C3B]">Message received</p>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-[#2F6B57]">
          {sent.configured
            ? "Thanks for reaching out. We've logged your message and our team will reply to your account email."
            : "Thanks for reaching out. We've logged your message. Our support inbox isn't fully set up yet, so a reply may take a little longer."}
        </p>
        <Button onClick={() => { setSent(null); setSubject(""); setMessage(""); }} data-testid="support-send-another" className="mt-5 min-h-11 rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643]">Send another message</Button>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="rounded-2xl pe-surface p-6" data-testid="support-form">
      <p className="flex items-center gap-2 font-serif text-xl text-[#171717]"><Mail className="h-5 w-5 text-[#2F6B57]" /> Contact support</p>
      <p className="mt-1 text-sm text-[#70584A]">Can't find your answer above? Send us a note and we'll help.</p>
      <div className="mt-4 space-y-4">
        <div>
          <label className={labelCls}>What's it about?</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="support-category" className={inputCls}>
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Subject</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="A short summary" data-testid="support-subject" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Message</label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} placeholder="Tell us what's going on. Please don't include card numbers or passwords." data-testid="support-message" className="mt-1 w-full rounded-xl pe-surface p-3.5 text-[#171717] outline-none focus:border-[#2F6B57]" />
        </div>
        <p className="flex items-start gap-1.5 text-xs leading-relaxed text-[#8A7C6B]">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> For your security, we never ask for passwords or full card
          numbers, and we don't include your balances in support emails.
        </p>
        <Button disabled={busy} type="submit" data-testid="support-submit" className="min-h-11 w-full rounded-xl bg-[#2F6B57] font-semibold text-white hover:bg-[#255643] sm:w-auto sm:px-7">Send message</Button>
      </div>
    </form>
  );
}

export default function HelpCenter() {
  useEffect(() => { track("help_opened"); }, []);
  return (
    <DashboardShell>
      <PageHead title="Help Center" sub="Answers, guidance, and a way to reach us." backToHome />

      <div className="mb-6 flex items-start gap-3 rounded-2xl border border-[#164C3B] bg-[#164C3B] p-5 text-white" data-testid="help-hero">
        <LifeBuoy className="mt-0.5 h-6 w-6 shrink-0 text-[#C9A86A]" />
        <div>
          <p className="font-serif text-xl">We're here to help.</p>
          <p className="mt-1 text-sm text-white/80">Most questions are answered below. If not, the contact form goes straight to our team.</p>
        </div>
      </div>

      <div className="rounded-2xl pe-surface p-2 sm:p-4" data-testid="help-faqs">
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`} className="border-[#EFEBE3]">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="px-3 text-left text-[15px] font-medium text-[#171717] hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="px-3 text-sm leading-relaxed text-[#70584A]">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      <div className="mt-8">
        <ContactForm />
      </div>
    </DashboardShell>
  );
}
